/**
 * Trivia Question Loader Service
 *
 * Hybrid approach:
 * 1. Primary: Load 1000 questions from /data/trivia-questions.json
 * 2. Fallback: Use the built-in 60-question bank (offline support)
 * 3. Cache: Store fetched questions in localStorage for subsequent offline use
 */

import { TriviaQuestion } from '../types';
import { questionBank as fallbackQuestions } from '../questionBank';

const CACHE_KEY = 'playhub_trivia_questions';
const CACHE_VERSION_KEY = 'playhub_trivia_questions_version';
const CURRENT_VERSION = '2'; // Increment when questions.json is updated

export class TriviaQuestionLoader {
  private static instance: TriviaQuestionLoader | null = null;
  private questions: TriviaQuestion[] | null = null;
  private loading: boolean = false;
  private loadPromise: Promise<TriviaQuestion[]> | null = null;

  static getInstance(): TriviaQuestionLoader {
    if (!TriviaQuestionLoader.instance) {
      TriviaQuestionLoader.instance = new TriviaQuestionLoader();
    }
    return TriviaQuestionLoader.instance;
  }

  /**
   * Load questions - tries JSON file first, then cache, then fallback
   */
  async loadQuestions(): Promise<TriviaQuestion[]> {
    // Return cached in-memory questions if already loaded
    if (this.questions && this.questions.length > 0) {
      return this.questions;
    }

    // Avoid multiple simultaneous fetches
    if (this.loading && this.loadPromise) {
      return this.loadPromise;
    }

    this.loading = true;
    this.loadPromise = this.doLoad();

    try {
      this.questions = await this.loadPromise;
      return this.questions;
    } finally {
      this.loading = false;
      this.loadPromise = null;
    }
  }

  private async doLoad(): Promise<TriviaQuestion[]> {
    // Step 1: Try to fetch from JSON file
    try {
      const response = await fetch('/data/trivia-questions.json');
      if (response.ok) {
        const data = await response.json();
        const questions = this.validateQuestions(data);
        if (questions.length > 0) {
          this.cacheQuestions(questions);
          return questions;
        }
      }
    } catch {
      // Network error or file not found - try cache
    }

    // Step 2: Try localStorage cache
    const cached = this.getCachedQuestions();
    if (cached && cached.length > 100) {
      return cached;
    }

    // Step 3: Fall back to built-in question bank
    return fallbackQuestions;
  }

  /**
   * Get questions by category and difficulty
   */
  async getQuestions(options?: {
    category?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    count?: number;
  }): Promise<TriviaQuestion[]> {
    const allQuestions = await this.loadQuestions();
    let filtered = [...allQuestions];

    if (options?.category) {
      filtered = filtered.filter(q => q.category === options.category);
    }

    if (options?.difficulty) {
      filtered = filtered.filter(q => q.difficulty === options.difficulty);
    }

    // Shuffle
    for (let i = filtered.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
    }

    if (options?.count) {
      filtered = filtered.slice(0, options.count);
    }

    return filtered;
  }

  /**
   * Get total question count
   */
  async getTotalCount(): Promise<number> {
    const questions = await this.loadQuestions();
    return questions.length;
  }

  /**
   * Get count breakdown by category
   */
  async getCategoryBreakdown(): Promise<Record<string, number>> {
    const questions = await this.loadQuestions();
    const breakdown: Record<string, number> = {};
    for (const q of questions) {
      breakdown[q.category] = (breakdown[q.category] || 0) + 1;
    }
    return breakdown;
  }

  private validateQuestions(data: any): TriviaQuestion[] {
    if (!Array.isArray(data)) return [];
    return data.filter((q: any) =>
      q.id &&
      q.category &&
      q.question &&
      Array.isArray(q.options) &&
      q.options.length >= 2 &&
      q.correctAnswer &&
      q.options.includes(q.correctAnswer) &&
      q.difficulty
    );
  }

  private cacheQuestions(questions: TriviaQuestion[]): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(questions));
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_VERSION);
    } catch {
      // localStorage full or unavailable - ignore
    }
  }

  private getCachedQuestions(): TriviaQuestion[] | null {
    try {
      const version = localStorage.getItem(CACHE_VERSION_KEY);
      if (version !== CURRENT_VERSION) return null;

      const stored = localStorage.getItem(CACHE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      return this.validateQuestions(parsed);
    } catch {
      return null;
    }
  }

  /**
   * Force refresh from JSON file (clear cache)
   */
  clearCache(): void {
    this.questions = null;
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_VERSION_KEY);
    } catch {
      // ignore
    }
  }
}
